<template>
    <DataTable
        :header="$t('deviationsFromRecommendedPriceInTime')"
        :fields="fields"
        :items="timeChartConfigurations"
        :loading="request.getLoading()"
        :hover="false"
        class="mb-4"
        thead-class="d-none"
        table-class="table-layout-fixed"
        :overflow="false"
    >
        <template #cell(chart)="{ item }">
            <EChart :options="item.chartOptions" type="time" />
        </template>
    </DataTable>
</template>



<script>
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';

    export default {
        name: 'DeviationsFromRecommendedPriceInTime',
        components: {
            EChart,
            DataTable,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
            };
        },
        computed: {
            fields() {
                return [{ key: 'chart', label: '' }];
            },
            rawProductsData() {
                return this.parentComponent.data?.products || {};
            },
            timeChartConfigurations() {
                const products = Object.values(this.rawProductsData);
                return products.map(({ product, price: history }) => {
                    const recommendedPrice = product.price;
                    // Guard przed niepoprawnym formatem historii
                    const records = Array.isArray(history) ? history : [];

                    // Parsujemy daty tylko raz i sortujemy po timestamp
                    const sortedHistory = records
                        .map(entry => ({
                            date: entry.date,
                            timestamp: new Date(entry.date).getTime(),
                            stores: Object.fromEntries(Object.entries(entry).filter(([key]) => key !== 'date')),
                        }))
                        .sort((a, b) => a.timestamp - b.timestamp);

                    // Zbieramy unikalne nazwy sklepów
                    const storeNames = Array.from(
                        sortedHistory.reduce((set, entry) => {
                            Object.keys(entry.stores).forEach(storeKey => set.add(storeKey));
                            return set;
                        }, new Set())
                    ).sort();

                    // Budujemy serię odniesienia (pozioma linia rekomendowanej ceny)
                    const firstDate = sortedHistory[0]?.date;
                    const lastDate = sortedHistory[sortedHistory.length - 1]?.date;
                    const referenceLineSeries = {
                        name: this.$t('recommendedPrice'),
                        type: 'line',
                        smooth: false,
                        data:
                            firstDate && lastDate
                                ? [
                                      [firstDate, recommendedPrice],
                                      [lastDate, recommendedPrice],
                                  ]
                                : [],
                        lineStyle: { color: 'black' },
                        symbol: 'none',
                        areaStyle: null,
                    };

                    // Generujemy serie czasowe dla każdego sklepu
                    const storeSeries = storeNames.map(storeName => {
                        const dataPoints = sortedHistory.map(({ date, stores }) => {
                            const rawPrice = stores[storeName] != null ? stores[storeName] : null;
                            const deviationValue = rawPrice != null && recommendedPrice != null ? rawPrice - recommendedPrice : null;
                            const deviationPercent = deviationValue != null && recommendedPrice !== 0 ? (deviationValue / recommendedPrice) * 100 : null;
                            return {
                                value: [date, rawPrice],
                                storeName,
                                rawPrice,
                                deviationValue,
                                deviationPercent,
                                recommendedPrice,
                            };
                        });

                        return {
                            name: storeName,
                            type: 'line',
                            showSymbol: true,
                            data: dataPoints,
                            areaStyle: null,
                            itemStyle: { opacity: 0 },
                            emphasis: { itemStyle: { opacity: 1 } },
                        };
                    });

                    // Zamrażamy chartOptions, aby Vue2 nie śledziło głębokiej reaktywności
                    const chartOptions = Object.freeze({
                        grid: { top: '80px' },
                        title: {
                            text: product.name,
                            subtext: `EAN: ${product.ean}`,
                            textStyle: { fontSize: 14 },
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: this.formatTimeTooltip,
                        },
                        xAxis: { type: 'time', boundaryGap: false },
                        yAxis: {
                            type: 'value',
                            name: this.$t('price'),
                            nameRotate: 90,
                            nameLocation: 'middle',
                        },
                        legend: {
                            data: [this.$t('recommendedPrice'), ...storeNames],
                        },
                        series: [referenceLineSeries, ...storeSeries],
                    });

                    return { chartOptions };
                });
            },
        },
        methods: {
            formatTimeTooltip(tooltipParams) {
                const pointData = tooltipParams.data;
                if (!pointData || pointData.rawPrice == null) {
                    return '';
                }

                const fmtCurrency = amount => (amount != null ? this.$format.currency(amount) : '-');
                const fmtPercent = amount => (amount != null ? this.$format.percent(amount) : '-');

                const deviationText = pointData.deviationValue != null ? `${pointData.deviationValue >= 0 ? '+' : ''}${fmtCurrency(pointData.deviationValue)}` : '-';
                const deviationPercentText = pointData.deviationPercent != null ? `${pointData.deviationPercent >= 0 ? '+' : ''}${fmtPercent(pointData.deviationPercent)}` : '-';
                const combinedDeviation = pointData.deviationValue != null ? `${deviationPercentText} (${deviationText})` : '-';

                return `
                <div style="display:flex;flex-direction:column;gap:3px;">
                    <div style="margin-bottom:2px;"><strong>${pointData.storeName}</strong></div>
                    <div style="display:flex;gap:10px;">
                        ${this.$t('price')}: <strong style="margin-left:auto">${fmtCurrency(pointData.rawPrice)}</strong>
                    </div>
                    <div style="display:flex;gap:10px;">
                        ${this.$t('recommendedPrice')}: <strong style="margin-left:auto">${fmtCurrency(pointData.recommendedPrice)}</strong>
                    </div>
                    <div style="display:flex;gap:10px;">
                        ${this.$t('deviation')}: <strong style="margin-left:auto">${combinedDeviation}</strong>
                    </div>
                    <div style="display:flex;gap:10px;">
                        ${this.$t('date')}: <strong style="margin-left:auto">${tooltipParams.value[0]}</strong>
                    </div>
                </div>
            `;
            },
        },
    };
</script>



<style lang="scss" scoped>
    .edit-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 10;
    }
</style>
